import { useAuth } from "@/hooks/useAuth"
import { lumi } from "@/lib/lumi"
import { useHead } from "@unhead/react"
import {Check, Copy, Loader2, LogOut, Shield, Snowflake} from 'lucide-react'
import { useCallback, useEffect, useState } from "react"
import { Link } from "react-router-dom"

type WaitlistSignup = {
  _id?: string
  email?: string
  source?: string
  status?: string
  createdAt?: string
}

function formatJoinedAt(value?: string) {
  if (!value) return "—"
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

function AdminWaitlist() {
  const { user, isAuthenticated, isAdmin } = useAuth()
  const [signups, setSignups] = useState<WaitlistSignup[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [copied, setCopied] = useState(false)
  const [signingIn, setSigningIn] = useState(false)

  useHead({
    title: "Waitlist — Penguin Pad Coming Soon",
    meta: [
      {
        name: "description",
        content: "Admin waitlist for Penguin Pad Coming Soon launch alerts.",
      },
    ],
  })

  const loadSignups = useCallback(async () => {
    setLoading(true)
    setError("")
    try {
      const { list } = await lumi.entities.waitlist_signups.list({
        sort: { createdAt: -1 },
        limit: 500,
      })
      setSignups(Array.isArray(list) ? list : [])
    } catch (loadError) {
      console.error("Waitlist fetch failed:", loadError)
      setError("Could not load waitlist emails. Sign in as an admin and try again.")
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!isAdmin) return
    void loadSignups()
  }, [isAdmin, loadSignups])

  const handleSignIn = async () => {
    setSigningIn(true)
    setError("")
    try {
      await lumi.auth.signIn()
    } catch (signInError) {
      console.error("Admin sign-in failed:", signInError)
      setError("Sign-in failed. Please try again.")
    } finally {
      setSigningIn(false)
    }
  }

  const handleCopyEmails = async () => {
    const emails = Array.from(
      new Set(
        signups
          .map((signup) => signup?.email?.trim().toLowerCase())
          .filter((email): email is string => Boolean(email)),
      ),
    )
    if (emails.length === 0) return

    try {
      await navigator.clipboard.writeText(emails.join("\n"))
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1800)
    } catch (copyError) {
      console.error("Copy failed:", copyError)
      setError("Could not copy emails. Select them manually instead.")
    }
  }

  return (
    <div className="min-h-screen bg-[#071018] text-white">
      <div className="mx-auto flex min-h-screen max-w-4xl flex-col px-5 py-6 sm:px-8">
        <header className="flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-white/15 bg-[#C8F542] text-[#10200A]">
              <Snowflake className="h-5 w-5" />
            </span>
            <div>
              <p className="font-display text-lg leading-none tracking-wide text-[#C8F542]">PENGUIN PAD</p>
              <p className="mt-1 text-[11px] uppercase tracking-[0.22em] text-cyan-100/80">Waitlist admin</p>
            </div>
          </Link>
          {isAuthenticated ? (
            <button
              type="button"
              onClick={() => lumi.auth.signOut()}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-black/35 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-cyan-100"
            >
              <LogOut className="h-3.5 w-3.5" />
              Sign out
            </button>
          ) : null}
        </header>

        <main className="flex flex-1 flex-col py-10">
          <section className="rounded-[28px] border border-white/15 bg-[#07151c]/80 p-5 shadow-[0_24px_80px_rgba(0,0,0,0.45)] backdrop-blur-xl sm:p-8">
            <div className="mb-6 flex items-center gap-2">
              <span className="inline-flex items-center gap-1 rounded-full bg-[#C8F542] px-3 py-1 text-[11px] font-black uppercase tracking-[0.2em] text-[#132109]">
                <Shield className="h-3 w-3" />
                Team only
              </span>
            </div>

            <h1 className="font-display text-4xl leading-none text-[#C8F542] sm:text-5xl">Launch alerts</h1>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-cyan-50/80">
              Emails collected on Penguin Pad Coming Soon live here so you can notify the list when bonding-curve
              presales open. Copy the list into your mail tool when you are ready.
            </p>

            {!isAuthenticated ? (
              <div className="mt-8">
                <button
                  type="button"
                  onClick={() => void handleSignIn()}
                  disabled={signingIn}
                  className="inline-flex h-12 items-center justify-center gap-2 rounded-2xl bg-[#C8F542] px-5 text-sm font-black uppercase tracking-[0.14em] text-[#132109] disabled:opacity-70"
                >
                  {signingIn ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {signingIn ? "Opening login" : "Sign in to view emails"}
                </button>
                {error ? <p className="mt-3 text-sm text-rose-300">{error}</p> : null}
              </div>
            ) : null}

            {isAuthenticated && !isAdmin ? (
              <p className="mt-8 text-sm text-rose-300">
                Signed in as {user?.email || "this account"}, but waitlist access is admin-only.
              </p>
            ) : null}

            {isAdmin ? (
              <div className="mt-8">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-sm text-cyan-100/80">
                    {loading ? "Loading signups..." : `${signups.length} email${signups.length === 1 ? "" : "s"} saved`}
                  </p>
                  <button
                    type="button"
                    onClick={() => void handleCopyEmails()}
                    disabled={loading || signups.length === 0}
                    className="inline-flex h-10 items-center gap-2 rounded-xl border border-[#C8F542]/40 bg-[#C8F542]/10 px-4 text-xs font-black uppercase tracking-[0.16em] text-[#C8F542] disabled:opacity-40"
                  >
                    {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                    {copied ? "Copied" : "Copy emails"}
                  </button>
                </div>

                {error ? <p className="mt-3 text-sm text-rose-300">{error}</p> : null}

                {loading ? (
                  <div className="mt-8 flex items-center gap-2 text-sm text-cyan-100/70">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Pulling the ice list...
                  </div>
                ) : signups.length === 0 ? (
                  <p className="mt-8 text-sm text-cyan-100/70">No waitlist emails yet. The pad is still quiet.</p>
                ) : (
                  <ul className="mt-6 divide-y divide-white/10 overflow-hidden rounded-2xl border border-white/10">
                    {signups.map((signup) => (
                      <li key={signup?._id || signup?.email} className="flex items-center justify-between gap-4 px-4 py-3">
                        <div>
                          <p className="text-sm font-semibold text-white">{signup?.email || "Unknown email"}</p>
                          <p className="mt-1 text-[11px] uppercase tracking-[0.16em] text-cyan-100/55">
                            {signup?.status || "subscribed"} · {formatJoinedAt(signup?.createdAt)}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : null}
          </section>
        </main>
      </div>
    </div>
  )
}

export default AdminWaitlist
